<template>
  <a-modal
    v-model:visible="visible"
    :footer="false"
    :mask="true"
    :mask-closable="true"
    :closable="true"
    class="account-modal"
    :width="480"
    unmount-on-close
    @cancel="handleClose"
  >
    <template #title>
      <div class="modal-header">
        <span class="modal-title">{{ t('account.title') }}</span>
      </div>
    </template>

    <div class="account-content">
      <!-- 用户信息展示 -->
      <div class="user-info">
        <a-avatar
          :size="64"
          :style="{ backgroundColor: 'rgb(var(--primary-6))' }"
        >
          {{
            email?.slice(0, 1).toUpperCase() ||
            nickname?.slice(0, 1).toUpperCase()
          }}
        </a-avatar>
        <div class="user-details">
          <div class="user-name">{{ nickname }}</div>
          <div class="user-email">{{ email }}</div>
        </div>
      </div>

      <!-- 姓名编辑 -->
      <div class="form-section">
        <div class="form-label">{{ t('account.name') }}</div>
        <div class="form-field">
          <a-input
            v-if="isEditingName"
            v-model="nameValue"
            :placeholder="t('account.namePlaceholder')"
            class="name-input"
          />
          <a-input
            v-else
            :model-value="nickname"
            :disabled="true"
            class="name-input"
          />
          <a-button
            v-if="!isEditingName"
            type="outline"
            class="edit-btn"
            @click="handleEditName"
          >
            {{ t('account.edit') }}
          </a-button>
          <div v-else class="edit-actions">
            <a-button type="outline" @click="handleCancelEdit">
              {{ t('account.cancel') }}
            </a-button>
            <a-button type="primary" :loading="saving" @click="handleSaveName">
              {{ t('account.save') }}
            </a-button>
          </div>
        </div>
      </div>

      <!-- 邮箱展示（只读） -->
      <div class="form-section">
        <div class="form-label">{{ t('account.email') }}</div>
        <a-input :model-value="email" :disabled="true" class="email-input" />
      </div>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import Message from '@/utils/message';
  import { useUserStore, useAppStore } from '@/store';
  import { updateUserInfoApi } from '@/api/user';

  const { t } = useI18n();
  const userStore = useUserStore();
  const appStore = useAppStore();

  const props = defineProps<{
    modelValue: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
  }>();

  const visible = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
  });

  const email = computed(() => userStore.email || '');
  const nickname = computed(() => userStore.nickname || '');

  const isEditingName = ref(false);
  const nameValue = ref('');
  const saving = ref(false);

  watch(
    () => props.modelValue,
    (isVisible) => {
      if (isVisible) {
        nameValue.value = nickname.value;
        isEditingName.value = false;
      }
    }
  );

  const handleEditName = () => {
    nameValue.value = nickname.value;
    isEditingName.value = true;
  };

  const handleCancelEdit = () => {
    nameValue.value = nickname.value;
    isEditingName.value = false;
  };

  const handleSaveName = async () => {
    if (!nameValue.value.trim()) {
      Message.warning(t('account.nameRequired'));
      return;
    }

    if (nameValue.value === nickname.value) {
      isEditingName.value = false;
      return;
    }

    saving.value = true;
    try {
      await updateUserInfoApi({
        nickname: nameValue.value.trim(),
      });
      // 更新 store 中的用户信息
      userStore.setInfo({
        nickname: nameValue.value.trim(),
      });
      Message.success(t('account.updateSuccess'));
      isEditingName.value = false;
    } finally {
      saving.value = false;
    }
  };

  const handleClose = () => {
    visible.value = false;
    isEditingName.value = false;
    nameValue.value = nickname.value;
  };
</script>

<style lang="less" scoped>
  .account-modal {
    :deep(.arco-modal-header) {
      padding: 20px 24px;
      border-bottom: 1px solid var(--color-border-2);
    }

    :deep(.arco-modal-body) {
      padding: 24px;
    }

    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .modal-title {
        font-size: 18px;
        font-weight: 600;
        color: var(--color-text-1);
      }
    }
  }

  .account-content {
    .user-info {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      margin-bottom: 32px;

      .user-details {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;

        .user-name {
          font-size: 18px;
          font-weight: 600;
          color: var(--color-text-1);
        }

        .user-email {
          font-size: 14px;
          color: var(--color-text-3);
        }
      }
    }

    .form-section {
      margin-bottom: 24px;

      .form-label {
        font-size: 14px;
        font-weight: 500;
        color: var(--color-text-1);
        margin-bottom: 8px;
      }

      .form-field {
        display: flex;
        align-items: center;
        gap: 12px;

        .name-input,
        .email-input {
          flex: 1;
        }

        .edit-btn {
          white-space: nowrap;
        }

        .edit-actions {
          display: flex;
          gap: 8px;
          white-space: nowrap;
        }
      }
    }
  }
</style>
